import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Watch,
  Activity,
  Clock,
  Compass,
  Heart,
  Flame,
  TrendingUp,
  Zap,
  ChevronRight,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { supabase } from '@/lib/customSupabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import {
  mergeActivityWithDerived,
  isRunningSport,
  isCyclingSport,
  formatDuration,
  formatPace,
  formatSpeedKmh,
  formatSportType,
} from '@/lib/garminSamples';

const formatRelativeTime = (dateStr) => {
  const date = new Date(dateStr);
  const diffSeconds = Math.round((Date.now() - date.getTime()) / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffMinutes / 60);
  const diffDays = Math.round(diffHours / 24);

  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

function getEnrichmentState(hasSamples, detailMeta) {
  if (hasSamples) return 'ready';
  if (detailMeta?.fit_storage_path) return 'fit_available';
  if (detailMeta?.fit_download_error) return 'fit_failed';
  return 'no_data';
}

const WearableData = () => {
  const { userId } = useParams();
  const { can } = useAuth();
  const [userName, setUserName] = useState('');
  const [connection, setConnection] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const fetchData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setLoadingActivities(true);

    const [profileRes, connRes, actRes, detailsRes] = await Promise.all([
      supabase.rpc('staff_get_user_profiles', { p_user_ids: [userId] }).single(),
      supabase
        .from('garmin_connections')
        .select(
          'status, garmin_user_id, connected_at, disconnected_at, last_synced_at, last_sync_status, last_error'
        )
        .eq('user_id', userId)
        .maybeSingle(),
      supabase
        .from('garmin_activities')
        .select(
          'id, garmin_activity_id, sport_type, started_at, duration_seconds, distance_meters, calories_kcal, avg_heart_rate_bpm, max_heart_rate_bpm, avg_speed_mps, avg_pace_sec_per_km, elevation_gain_meters'
        )
        .eq('user_id', userId)
        .order('started_at', { ascending: false })
        .limit(100),
      supabase
        .from('garmin_activity_details')
        .select('garmin_activity_id, samples_5s, detail_metadata')
        .eq('user_id', userId),
    ]);

    setUserName(profileRes.data?.full_name || 'Unknown');
    setConnection(connRes.data || null);
    setLoading(false);

    const detailsByGarminId = Object.fromEntries(
      (detailsRes.data || []).map((d) => [
        d.garmin_activity_id,
        { samples_5s: d.samples_5s, detail_metadata: d.detail_metadata },
      ])
    );

    const merged = (actRes.data || []).map((a) => {
      const detail = detailsByGarminId[a.garmin_activity_id];
      const act = mergeActivityWithDerived(a, detail?.samples_5s);
      act._detailMetadata = detail?.detail_metadata || null;
      act._enrichState = getEnrichmentState(act.hasSamples, detail?.detail_metadata);
      return act;
    });
    setActivities(merged);
    setLoadingActivities(false);
  }, [userId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBulkEnrich = useCallback(async () => {
    if (!userId || bulkEnriching) return;
    setBulkEnriching(true);
    setBulkResult(null);

    try {
      const { data, error } = await supabase.functions.invoke('garmin-fit-enrich', {
        body: { userId, mode: 'bulk' },
      });
      if (error) throw error;
      setBulkResult(data);
      await fetchData();
    } catch (err) {
      setBulkResult({ status: 'error', reason: err.message });
    } finally {
      setBulkEnriching(false);
    }
  }, [userId, bulkEnriching, fetchData]);

  const sportTypes = [...new Set(activities.map((a) => a.sport_type))];
  const totalDistance = activities.reduce(
    (sum, a) => sum + (Number(a.distance_meters) || 0),
    0
  );
  const totalDuration = activities.reduce(
    (sum, a) => sum + (Number(a.duration_seconds) || 0),
    0
  );
  const totalCalories = activities.reduce(
    (sum, a) => sum + (Number(a.calories_kcal) || 0),
    0
  );

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-6xl mx-auto">
      <Helmet>
        <title>Wearable Data · {userName}</title>
      </Helmet>

      <Button variant="outline" size="sm" asChild>
        <Link to={`/user-activity/${userId}`}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to {userName || 'User'}
        </Link>
      </Button>

      <header className="flex flex-col sm:flex-row items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-3">
            <Watch className="h-6 w-6" />
            Wearable Data
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{userName}</p>
        </div>
        {loading ? (
          <Skeleton className="h-6 w-32" />
        ) : connection?.status === 'connected' ? (
          <Badge variant="success">Garmin Connected</Badge>
        ) : connection?.status === 'expired' || connection?.status === 'error' ? (
          <Badge variant="warning">Garmin {connection.status}</Badge>
        ) : (
          <Badge variant="secondary">Not Connected</Badge>
        )}
      </header>

      {loading ? (
        <div className="grid gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-12 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : connection ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Status
              </p>
              <Badge
                variant={
                  connection.status === 'connected'
                    ? 'success'
                    : connection.status === 'expired' || connection.status === 'error'
                      ? 'warning'
                      : 'secondary'
                }
              >
                {connection.status}
              </Badge>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Connected Since
              </p>
              <p className="text-sm font-medium">
                {connection.connected_at
                  ? new Date(connection.connected_at).toLocaleDateString()
                  : '—'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Last Sync
              </p>
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">
                  {connection.last_synced_at
                    ? formatRelativeTime(connection.last_synced_at)
                    : 'Never'}
                </p>
                {connection.last_sync_status && (
                  <Badge
                    variant={
                      connection.last_sync_status === 'success'
                        ? 'success'
                        : connection.last_sync_status === 'partial'
                          ? 'warning'
                          : 'destructive'
                    }
                    className="text-[10px]"
                  >
                    {connection.last_sync_status}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                Garmin User ID
              </p>
              <p className="text-sm font-medium font-mono truncate">
                {connection.garmin_user_id || '—'}
              </p>
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card>
          <CardContent className="p-8 text-center">
            <Watch className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              No Garmin connection found for this user.
            </p>
          </CardContent>
        </Card>
      )}

      {!loadingActivities && activities.length > 0 && (
        <>
          <Separator />
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Total Activities
                </p>
                <p className="text-2xl font-bold">{activities.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Total Distance
                </p>
                <p className="text-2xl font-bold">
                  {(totalDistance / 1000).toFixed(1)}{' '}
                  <span className="text-sm font-normal text-muted-foreground">km</span>
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Total Duration
                </p>
                <p className="text-2xl font-bold">{formatDuration(totalDuration) || '—'}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Total Calories
                </p>
                <p className="text-2xl font-bold">
                  {totalCalories > 0 ? Math.round(totalCalories).toLocaleString() : '—'}{' '}
                  {totalCalories > 0 && (
                    <span className="text-sm font-normal text-muted-foreground">kcal</span>
                  )}
                </p>
              </CardContent>
            </Card>
          </div>

          {sportTypes.length > 1 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-muted-foreground mr-1 self-center">Sport types:</span>
              {sportTypes.map((t) => (
                <Badge key={t} variant="outline" className="capitalize">
                  {formatSportType(t)}
                </Badge>
              ))}
            </div>
          )}
        </>
      )}

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-medium text-muted-foreground">Recent Activities</h2>
          {!loadingActivities && (() => {
            const missingCount = activities.filter((a) => a._enrichState === 'fit_available').length;
            if (missingCount === 0 || !can('wearables.enrich')) return null;
            return (
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkEnrich}
                disabled={bulkEnriching}
              >
                {bulkEnriching ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Parsing FIT...
                  </>
                ) : (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Parse FIT ({missingCount})
                  </>
                )}
              </Button>
            );
          })()}
          {bulkResult && bulkResult.status === 'bulk_complete' && (
            <span className="text-xs text-muted-foreground ml-2">
              {bulkResult.succeeded} parsed, {bulkResult.failed} failed
            </span>
          )}
        </div>
        {loadingActivities ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : activities.length > 0 ? (
          <div className="space-y-2">
            {activities.map((a) => (
              <Link
                key={a.id}
                to={`/user-activity/${userId}/wearable/${a.id}`}
                className="block"
              >
                <Card className="cursor-pointer transition-colors hover:bg-muted/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                          <Activity className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium capitalize truncate">
                            {formatSportType(a.sport_type)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(a.started_at).toLocaleDateString(undefined, {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                            {' · '}
                            {new Date(a.started_at).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                            {a.hasSamples ? (
                              <span className="ml-1 text-muted-foreground/80">
                                · {a.sampleCount} samples
                              </span>
                            ) : a._enrichState === 'fit_available' ? (
                              <Badge variant="warning" className="ml-1.5 text-[9px] px-1 py-0">FIT</Badge>
                            ) : (
                              <Badge variant="destructive" className="ml-1.5 text-[9px] px-1 py-0">No data</Badge>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground flex-shrink-0">
                        {a.duration_seconds != null && (
                          <div className="flex items-center gap-1.5" title="Duration">
                            <Clock className="h-3.5 w-3.5" />
                            <span>{formatDuration(a.duration_seconds)}</span>
                          </div>
                        )}
                        {a.distance_meters != null && a.distance_meters > 0 && (
                          <div className="flex items-center gap-1.5" title="Distance">
                            <Compass className="h-3.5 w-3.5" />
                            <span>{(a.distance_meters / 1000).toFixed(2)} km</span>
                          </div>
                        )}
                        {a.avg_heart_rate_bpm != null && (
                          <div className="flex items-center gap-1.5 hidden sm:flex" title="Avg HR">
                            <Heart className="h-3.5 w-3.5" />
                            <span>
                              {a.avg_heart_rate_bpm}
                              {a.max_heart_rate_bpm ? `/${a.max_heart_rate_bpm}` : ''} bpm
                            </span>
                          </div>
                        )}
                        {isCyclingSport(a.sport_type) && a.avg_power_watts != null && (
                          <div className="flex items-center gap-1.5 hidden md:flex" title="Avg Power">
                            <Zap className="h-3.5 w-3.5" />
                            <span>{a.avg_power_watts} W</span>
                          </div>
                        )}
                        {isCyclingSport(a.sport_type) &&
                          formatSpeedKmh(a.avg_speed_mps) && (
                            <div
                              className="flex items-center gap-1.5 hidden lg:flex"
                              title="Avg Speed"
                            >
                              <TrendingUp className="h-3.5 w-3.5" />
                              <span>{formatSpeedKmh(a.avg_speed_mps)}</span>
                            </div>
                          )}
                        {isRunningSport(a.sport_type) && formatPace(a.avg_pace_sec_per_km) && (
                          <div className="flex items-center gap-1.5 hidden lg:flex" title="Avg Pace">
                            <TrendingUp className="h-3.5 w-3.5" />
                            <span>{formatPace(a.avg_pace_sec_per_km)}</span>
                          </div>
                        )}
                        {a.calories_kcal != null && a.calories_kcal > 0 && (
                          <div className="flex items-center gap-1.5 hidden xl:flex" title="Calories">
                            <Flame className="h-3.5 w-3.5" />
                            <span>{Math.round(a.calories_kcal)} kcal</span>
                          </div>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <Activity className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No Garmin activities synced yet.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default WearableData;
